import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { ArrowLeft, Printer, Save, User, ShieldCheck, Calendar, ArrowRight, ScrollText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "../../lib/api";
import { fmtDate } from "../../lib/statuses";

const TERMS = [
  ["This replacement is issued for the verified warranty case referenced in this report.", "تم إصدار هذا الاستبدال بناءً على حالة الضمان المعتمدة في هذا التقرير."],
  ["The replacement unit is covered according to the Warranty Period stated above.", "تخضع القطعة البديلة لفترة الضمان الموضحة أعلاه."],
  ["Proof of purchase / reference invoice may be required for any future warranty service.", "قد يُطلب إثبات الشراء أو الفاتورة المرجعية لأي خدمة ضمان مستقبلية."],
  ["Misuse, unauthorized repair, liquid exposure and physical damage are not covered.", "لا يغطي الضمان سوء الاستخدام أو الإصلاح غير المصرح به أو التعرض للسوائل أو التلف."],
  ["IDEA may inspect and verify any future warranty claim in accordance with the applicable warranty policy.", "يحق لـ IDEA فحص والتحقق من أي مطالبة ضمان مستقبلية وفقاً لسياسة الضمان."],
];

function Logo() {
  return (
    <img src="/brand/idea-logo.png" alt="IDEA" data-testid="report-logo-img" style={{ height: "45px", width: "auto", display: "block" }} />
  );
}

function InfoRow({ en, ar, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-[3px]">
      <div className="flex items-baseline gap-2 text-[12px] text-slate-500">
        <span className="font-semibold text-slate-600">{en}</span>
        <span dir="rtl" className="text-slate-400">{ar}</span>
      </div>
      <div className="text-right text-[13px] font-bold text-slate-900">{value || "—"}</div>
    </div>
  );
}

function PanelRow({ en, ar, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 py-[4px] last:border-0">
      <div className="flex items-baseline gap-2 text-[12px]">
        <span className="font-medium text-slate-600">{en}</span>
        <span dir="rtl" className="text-slate-400">{ar}</span>
      </div>
      <div className={`text-right text-[13px] font-semibold text-slate-900 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}

export default function ReplacementReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [warranty, setWarranty] = useState("");
  const [ownership, setOwnership] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/replacements/${id}/report`);
    setData(data);
    setWarranty(data.report.warranty_period || "");
    setOwnership(!!data.report.include_ownership_clause);
  };
  useEffect(() => { load(); }, [id]);

  const save = async () => {
    if (!warranty.trim()) { toast.error("Enter a Warranty Period before generating the report"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/replacements/${id}/report`, { warranty_period: warranty, include_ownership_clause: ownership });
      setData(data);
      toast.success("Report saved — warranty period stored");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-10 text-center text-slate-400">Loading report...</div>;
  const r = data.report;
  const sender = data.sender || {};
  const canPrint = !!r.warranty_period;
  const origImg = r.original?.image ? fileUrl(r.original.image) : null;
  const replImg = r.replacement?.image ? fileUrl(r.replacement.image) : null;
  const addr2 = [sender.street, sender.city].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-slate-200 print:bg-white">
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print {
          html, body { width: 210mm; margin: 0 !important; padding: 0 !important; background: #fff !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .report-page { width: 210mm; min-height: 297mm; margin: 0 !important; box-shadow: none !important; border: none !important; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div className="no-print sticky top-0 z-10 flex flex-wrap items-end gap-3 border-b border-slate-300 bg-white px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate("/admin/replacements")} className="gap-1.5"><ArrowLeft className="h-4 w-4" /> Back</Button>
        <div className="flex flex-1 items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <Label className="mb-1 block text-xs">Warranty Period (free text — e.g. 90 Days, 1 Year, Until 15 Oct 2027)</Label>
            <Input data-testid="warranty-period-input" value={warranty} onChange={(e) => setWarranty(e.target.value)} placeholder="Enter warranty period" />
          </div>
          <label className="flex items-center gap-2 pb-2 text-xs text-slate-600">
            <Checkbox checked={ownership} onCheckedChange={(v) => setOwnership(!!v)} data-testid="ownership-clause-check" /> Include ownership clause
          </label>
        </div>
        <Button size="sm" onClick={save} disabled={saving} data-testid="save-report-btn" className="gap-1.5 bg-slate-700 hover:bg-slate-800"><Save className="h-4 w-4" /> {saving ? "Saving..." : "Save"}</Button>
        <Button size="sm" onClick={() => window.print()} disabled={!canPrint} data-testid="print-report-btn" className="gap-1.5 bg-blue-600 hover:bg-blue-700"><Printer className="h-4 w-4" /> Print / Save PDF</Button>
      </div>
      {!canPrint && <div className="no-print mx-auto mt-3 w-[210mm] max-w-full px-2 text-sm text-amber-600">Enter a Warranty Period and click Save to finalize before printing.</div>}

      <div className="mx-auto my-0 w-[210mm] max-w-full overflow-x-auto md:my-5 print:!my-0 print:overflow-visible">
        <div className="report-page relative mx-auto w-[210mm] bg-white text-slate-900 shadow-lg" style={{ minHeight: "297mm" }} data-testid="customer-report" id="report">
          {/* HEADER — edge to edge */}
          <div className="flex items-center justify-between bg-[#0a0a0a] px-[14mm] py-3">
            <Logo />
            <div className="text-right text-[11px] leading-tight text-slate-200">
              <div className="font-bold tracking-wide text-white">{sender.company_name || "IDEA STORE L.L.C"}</div>
              {addr2 && <div>{addr2}</div>}
              {sender.country && <div>{sender.country}</div>}
            </div>
          </div>

          <div className="px-[14mm] py-4">
            {/* TITLE + INFO PANEL */}
            <div className="flex items-start justify-between gap-6">
              <img src="/brand/warranty-report-title.png" alt="Warranty Replacement Report" data-testid="report-title-img" className="self-center" style={{ height: "22mm", width: "auto", display: "block" }} />
              <div className="min-w-[280px] rounded-lg bg-slate-50 px-4 py-3">
                <InfoRow en="RMA NO." ar="رقم الضمان" value={r.rma_number} />
                <InfoRow en="REPORT DATE" ar="تاريخ التقرير" value={fmtDate(r.report_date)} />
                <InfoRow en="ORDER NO." ar="رقم الطلب" value={r.order_number} />
                <InfoRow en="CLAIM DATE" ar="تاريخ الاستلام" value={fmtDate(r.claim_date)} />
              </div>
            </div>

            {/* CUSTOMER + SERVICE */}
            <div className="mt-4 grid grid-cols-2 gap-5">
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2">
                  <User className="h-4 w-4 text-slate-700" />
                  <span className="text-[13px] font-bold uppercase tracking-wide text-slate-900">Customer Information</span>
                  <span dir="rtl" className="ml-auto text-[12px] text-slate-400">معلومات العميل</span>
                </div>
                <PanelRow en="Customer Name" ar="اسم العميل" value={r.customer_name} />
                <PanelRow en="WhatsApp / Contact" ar="التواصل" value={r.mobile} mono />
              </div>
              <div className="rounded-xl border border-slate-200 p-3">
                <div className="mb-3 flex items-center gap-2 border-b border-slate-100 pb-2">
                  <ShieldCheck className="h-4 w-4 text-slate-700" />
                  <span className="text-[13px] font-bold uppercase tracking-wide text-slate-900">Service Centre Period</span>
                  <span dir="rtl" className="ml-auto text-[12px] text-slate-400">فترة مركز الخدمة</span>
                </div>
                <PanelRow en="Received Date" ar="تاريخ الاستلام" value={fmtDate(r.service_start)} />
                <PanelRow en="Completion Date" ar="تاريخ الإكمال" value={r.service_completion ? fmtDate(r.service_completion) : "—"} />
                <PanelRow en="Total Period" ar="المدة الإجمالية" value={r.service_days === "" ? "—" : `${r.service_days} day${r.service_days === 1 ? "" : "s"}`} />
                <div className="mt-2 flex items-center justify-between rounded-lg bg-slate-100 px-3 py-2">
                  <div className="flex items-center gap-2 text-[12px]">
                    <Calendar className="h-4 w-4 text-slate-600" />
                    <span className="font-semibold text-slate-700">Warranty Period</span>
                    <span dir="rtl" className="text-slate-400">فترة الضمان</span>
                  </div>
                  <span data-testid="report-warranty-period" className="text-[13px] font-bold text-slate-900">{r.warranty_period || "—"}</span>
                </div>
              </div>
            </div>

            {/* PRODUCT COMPARISON */}
            <div className="relative mt-5 grid grid-cols-2 gap-5">
              <ProductPanel heading="ORIGINAL PRODUCT" ar="المنتج الأصلي" img={origImg}>
                <PanelRow en="Product Name" ar="اسم المنتج" value={r.original.product_name} />
                <PanelRow en="Part Name" ar="اسم القطعة" value={r.original.part_name} />
                <PanelRow en="Brand" ar="الماركة" value={r.original.brand} />
                <PanelRow en="Serial Number" ar="الرقم التسلسلي" value={r.original.serial} mono />
                <PanelRow en="Problem / Fault" ar="المشكلة" value={r.fault} />
              </ProductPanel>
              <div className="absolute left-1/2 top-[90px] z-10 -translate-x-1/2 rounded-full bg-white p-1 shadow-sm">
                <ArrowRight className="h-5 w-5 text-slate-400" />
              </div>
              <ProductPanel heading="REPLACEMENT PRODUCT" ar="المنتج البديل" img={replImg}>
                <PanelRow en="Replacement Product" ar="المنتج البديل" value={r.replacement.product_name} />
                <PanelRow en="Replacement Serial" ar="الرقم التسلسلي" value={r.replacement.serial} mono />
                <PanelRow en="Received Date" ar="تاريخ الاستلام" value={r.replacement.received_date ? fmtDate(r.replacement.received_date) : "—"} />
                <PanelRow en="Customer Handover" ar="التسليم للعميل" value={r.replacement.handover_date ? fmtDate(r.replacement.handover_date) : "—"} />
              </ProductPanel>
            </div>

            {/* TERMS */}
            <div className="mt-4 rounded-xl border border-slate-200 p-3">
              <div className="mb-3 flex items-center gap-2">
                <ScrollText className="h-4 w-4 text-slate-700" />
                <span className="text-[13px] font-bold uppercase tracking-wide text-slate-900">Terms &amp; Policy</span>
                <span dir="rtl" className="ml-auto text-[12px] text-slate-400">الشروط والسياسات</span>
              </div>
              <div className="space-y-2">
                {TERMS.map(([en, ar], i) => (
                  <div key={i} className="grid grid-cols-[22px_1fr_1fr] items-start gap-3 text-[11.5px] leading-snug">
                    <span className="flex h-5 w-5 items-center justify-center rounded bg-slate-900 text-[10px] font-bold text-white">{i + 1}</span>
                    <span className="text-slate-600">{en}</span>
                    <span dir="rtl" className="text-right text-slate-600">{ar}</span>
                  </div>
                ))}
                {r.include_ownership_clause && (
                  <div className="grid grid-cols-[22px_1fr_1fr] items-start gap-3 text-[11.5px] leading-snug">
                    <span className="flex h-5 w-5 items-center justify-center rounded bg-slate-900 text-[10px] font-bold text-white">{TERMS.length + 1}</span>
                    <span className="text-slate-600">The original faulty unit is retained by IDEA as part of this warranty resolution.</span>
                    <span dir="rtl" className="text-right text-slate-600">يتم الاحتفاظ بالوحدة المعطلة الأصلية لدى IDEA كجزء من تسوية الضمان.</span>
                  </div>
                )}
              </div>
            </div>

            {/* SIGNATURES */}
            <div className="mt-5 grid grid-cols-2 gap-8">
              <SignatureBox en="IDEA REPRESENTATIVE" ar="مندوب IDEA" />
              <SignatureBox en="CUSTOMER" ar="العميل" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProductPanel({ heading, ar, img, children }) {
  return (
    <div className="overflow-hidden rounded-xl border border-slate-200">
      <div className="flex items-center justify-between bg-[#0a0a0a] px-4 py-2.5">
        <span className="text-[13px] font-bold uppercase tracking-wide text-white">{heading}</span>
        <span dir="rtl" className="text-[12px] text-slate-300">{ar}</span>
      </div>
      {img && (
        <div className="flex items-center justify-center border-b border-slate-100 bg-slate-50 p-3" style={{ height: "120px" }}>
          <img src={img} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
      <div className="px-4 py-2">{children}</div>
    </div>
  );
}

function SignatureBox({ en, ar }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <span className="text-[12px] font-bold uppercase tracking-wide text-slate-900">{en}</span>
        <span dir="rtl" className="text-[12px] text-slate-400">{ar}</span>
      </div>
      <div className="mt-6 border-t border-slate-400 pt-1 text-[11px] text-slate-500">
        Name &amp; Signature <span dir="rtl" className="float-right">الاسم والتوقيع</span>
      </div>
    </div>
  );
}
